import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip, Link as MuiLink } from '@mui/material';
import Box from '@mui/material/Box';

import { palette, spaces } from '../../../../../assets/styles/themes/tokens';
import { compressClassYearArray } from '../../../../../utils/compressClassYearArray';
import useRouter from '../../../../../utils/useRouter';
import useOpenInNewTabExperiment from '../../../../../utils/useOpenInNewTabExperiment';
import { getCardLinkProps, indexToPosition } from '../../../../../utils/searchUtils';
import Tooltip from '../../../../Tooltip';
import { AddMenuButton, getItemLink, mapContentTypeName, Metadata, MuiCardBaseProps } from '../MuiCardBase';
import CardWrapper from './MuiCardBaseLongWrapper';
import CardSection from './MuiCardBaseLongSection';
import CardContent from './MuiCardBaseLongContent';
import CardHeader from './MuiCardBaseLongHeader';
import CardTextsWrapper from './MuiCardBaseLongTextsWrapper';
import CardTitle from './MuiCardBaseLongTitle';
import CardDescription from '../../../common/CardDescription/NewCardDescription';
import CardMetadataWrapper from './MuiCardBaseLongMetadataWrapper';
import AiMainTopic from '../AiMainTopic';
import CardCover from '../MuiCardBaseCover';

export const MuiCardBaseLong: React.FC<MuiCardBaseProps> = (props) => {
  const {
    id,
    contentType,
    publisher,
    index,
    isReadOnly,
    cover,
    title,
    description,
    subjects,
    classYears,
    schoolTypes,
    link,
    linkFunction,
    date,
    chipText,
    isPinned,
    isNew,
    itemData,
    isNewAddMenuButtonMenu,
    onShowNotification,
    isFirstElement,
    aiMainTopic,
    aiProductItemSubject,
    aiProductItemClassYears,
    aiProductItemSchoolType,
    mindItemPageNumber,
    secondaryLink
  } = props;

  const { t } = useTranslation();
  const { router, match } = useRouter();
  const [isExpanded, setIsExpanded] = useState(false);
  const openInNewTab = useOpenInNewTabExperiment();
  const itemLink = getItemLink({ id, contentType, mindItemPageNumber });
  const newTabHref = openInNewTab
    ? router.createHref(
      getCardLinkProps(itemLink, match.location.pathname, undefined, {
        currentSearch: match.location.search,
        openInNewTab: true,
      }),
    )
    : undefined;

  return (
    <CardWrapper
      contentType={contentType}
      itemLink={itemLink}
      isNew={isNew}
      isExpanded={isExpanded}
      openInNewTab={openInNewTab}
      newTabHref={newTabHref}
    >
      <AddMenuButton
        isPinned={isPinned}
        type={contentType}
        id={id}
        itemData={itemData}
        index={index}
        isNewAddMenuButtonMenu={isNewAddMenuButtonMenu}
        onShowNotification={onShowNotification}
        isFirstElement={isFirstElement}
      />
      <CardCover
        contentType={contentType}
        itemLink={itemLink}
        cover={cover}
        isWide={contentType === 'video'}
        withPlayIcon={contentType === 'video'}
        title={title}
        index={index}
        openInNewTab={openInNewTab}
      >
        <Box
          data-testid='mui-card-desktop__pages-chip'
          sx={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            bottom: spaces.md,
          }}
        >
          {contentType !== 'video' && (
            <Chip label={chipText} color='secondary' size='small' />
          )}
        </Box>
      </CardCover>
      <CardSection>
        <CardContent>
          <CardHeader
            primaryText={t(mapContentTypeName(contentType))}
            secondaryText={publisher}
            showReadOnlyChip={isReadOnly}
            customReadOnlySx={{ marginY: '-5.5px' }}
          />
          <CardTextsWrapper>
            <CardTitle title={title} itemLink={itemLink} index={index} openInNewTab={openInNewTab} />
            {aiMainTopic && <AiMainTopic topic={aiMainTopic}/>}
            <CardDescription 
              description={description}
              isExpanded={isExpanded} 
              onShowToggleClick={() => setIsExpanded(!isExpanded)} />
          </CardTextsWrapper>
        </CardContent>
        <CardMetadataWrapper>
          {aiProductItemSubject && aiProductItemSubject.length > 0 ? (
            <SubjectsMetadata subjects={aiProductItemSubject} />
          ) : (
            subjects && subjects.length > 0 && (
              <SubjectsMetadata subjects={subjects} />
            )
          )}
          {aiProductItemClassYears && aiProductItemClassYears.length > 0 ? (
            <ClassYearsMetadata classYears={aiProductItemClassYears.map(Number)} />
          ) : (
            classYears && classYears.length > 0 && (
              <ClassYearsMetadata classYears={classYears} />
            )
          )}
          {aiProductItemSchoolType && aiProductItemSchoolType.length > 0 ? (
            <SchoolTypesMetadata schoolTypes={aiProductItemSchoolType} />
          ) : (
            schoolTypes && schoolTypes.length > 0 && (
              <SchoolTypesMetadata schoolTypes={schoolTypes} />
            )
          )}
          {date && <Metadata icon='calendar' label={date} />}
          {link && (
            <LinkSection
              link={link}
              linkFunction={linkFunction}
              index={index}
              openInNewTab={openInNewTab}
            />
          )}
          {secondaryLink && (
            <LinkSection link={secondaryLink} index={index} openInNewTab={openInNewTab} />
          )}
        </CardMetadataWrapper>
      </CardSection>
    </CardWrapper>
  );
};

const LinkSection = ({
  link,
  linkFunction,
  index,
  openInNewTab,
}: {
  link: NonNullable<MuiCardBaseProps['link']>;
  linkFunction?: () => void;
  index?: number;
  openInNewTab?: boolean;
}) => {
  const { router, match } = useRouter();

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => {
    e.stopPropagation();
    const linkTo = getCardLinkProps(
      to,
      match.location.pathname,
      indexToPosition(index),
      { currentSearch: match.location.search, openInNewTab },
    );
    if (linkFunction) {
      linkFunction();
      return;
    }
    if (openInNewTab) {
      window.open(router.createHref(linkTo), '_blank', 'noopener,noreferrer');
      return;
    }
    router.push(linkTo);
  };

  return (
    <Tooltip
      label={link.label}
      open={link.hasTooltip ? undefined : false}
      placement='top-start'
    >
      <MuiLink
        underline='none'
        onClick={(e) => handleLinkClick(e, link.to)}
        data-testid='mui-card-link'
      >
        <Metadata
          icon={link.icon}
          label={link.label}
          sx={{ color: undefined, stroke: undefined }}
        />
      </MuiLink>
    </Tooltip>
  );
};

const SubjectsMetadata = ({
  subjects,
}: {
  subjects: NonNullable<MuiCardBaseProps['subjects']>;
}) => (
  <Tooltip label={subjects.join(', ')} placement='top-start'>
    <Metadata
      icon='tag'
      sx={{ color: palette.text.secondary, maxWidth: 218 }}
      label={subjects.join(', ')}
    />
  </Tooltip>
);

const ClassYearsMetadata = ({
  classYears,
}: {
  classYears: NonNullable<MuiCardBaseProps['classYears']>;
}) => {
  const { t } = useTranslation();

  return (
    <Metadata
      icon='teacher'
      sx={{ color: palette.text.secondary }}
      label={`${compressClassYearArray(classYears).join(', ')} ${t('Class year')}`}
    />
  );
};

const SchoolTypesMetadata = ({
  schoolTypes,
}: {
  schoolTypes: NonNullable<MuiCardBaseProps['aiProductItemSchoolType']>;
}) => (
  <Tooltip label={schoolTypes.join(', ')} placement='top-start'>
    <Metadata
      icon='schoolType'
      sx={{ color: palette.text.secondary, maxWidth: 218 }}
      label={schoolTypes.join(', ')}
    />
  </Tooltip>
);

export default MuiCardBaseLong;
